import { inject, Injectable } from "@angular/core";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { FiskilApi } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.api";
import { FiskilRevokeConsentsResponse } from "@bitwarden/web-vault/app/services/syncing/fiskil/types/api.types";
import {
  FISKIL_PLUGIN_NAME,
  PluginConfigService,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";

@Injectable({
  providedIn: "root",
})
export class FiskilConsentService {
  private fiskilApi = inject(FiskilApi);
  private userStore = inject(UserStoreService);
  private transactionStore = inject(TransactionStoreService);
  private pluginConfigService = inject(PluginConfigService);
  private i18nService = inject(I18nService);
  private platformUtilsService = inject(PlatformUtilsService);
  private logService = inject(LogService);

  async removeConsentAndData(): Promise<FiskilRevokeConsentsResponse> {
    const result = await this.fiskilApi.revokeConsents();

    if (result.failed?.length > 0) {
      const failedCount = result.failed.length;
      this.platformUtilsService.showToast(
        "warning",
        null,
        this.i18nService.t(
          "fiskilConsentPartiallyRevoked",
          result.revoked.toString(),
          failedCount.toString(),
        ),
      );
    } else if (result.revoked > 0) {
      const fiskilAccounts = this.userStore.getFiskilAccounts();
      if (fiskilAccounts.length > 0) {
        const isDataDeleted = await this.transactionStore.purgeMultipleAccounts(fiskilAccounts);
        if (!isDataDeleted) {
          this.platformUtilsService.showToast(
            "error",
            "Error Occurred",
            this.i18nService.t("consentRemovedButDataNot"),
          );
          this.logService.error(
            "Fiskil consent removed but accounts/transactions could not be deleted",
          );
        }
      }
      this.platformUtilsService.showToast(
        "success",
        null,
        this.i18nService.t("fiskilConsentRevokedSuccessfully"),
      );
    } else {
      this.platformUtilsService.showToast(
        "info",
        null,
        this.i18nService.t("fiskilNoActiveConsents"),
      );
    }

    await this.pluginConfigService.deletePluginByName(FISKIL_PLUGIN_NAME);

    return result;
  }
}
